import { FunctionComponent } from "react";

import { capitalize } from "lodash/fp";

import {
  Button as MuiButton,
  ButtonProps,
  Input,
  InputProps,
  LinearProgress,
  LinearProgressProps,
  styled,
} from "@mui/material";

import { BillingCadence } from "@app/common/types";

import Chip from "@rui/Chip";
import Stack, { Row, StackProps } from "@rui/Stack";

export const BillingWrapper = styled(Stack)({
  width: "100%",
  backgroundColor: "#FFF",
});

type BillingCardProps = StackProps & { testId?: string };

export const BillingCard = styled<FunctionComponent<BillingCardProps>>(
  ({ testId, ...props }) => <Stack data-test-id={testId} {...props} />
)(({ theme }) => ({
  border: "solid 1px",
  borderColor: theme.palette.shades.neutral[100],
  borderRadius: "8px",
  height: "100%",
  minHeight: "200px",
}));

export const BillingCardHeader = styled((props) => (
  <Row variant="apart" {...props} />
))<StackProps>(({ theme }) => ({
  backgroundColor: theme.palette.transparent.black[2],
  borderBottom: `solid 1px ${theme.palette.shades.neutral[100]}`,
  padding: theme.spacing(1.5),
}));

export const BillingCardContent = styled(Stack)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(1.5),
  justifyContent: "space-between",
}));

export const BillingCardActions = styled(Row)(({ theme }) => ({
  borderTop: `solid 1px ${theme.palette.shades.neutral[100]}`,
  paddingTop: theme.spacing(1.5),
}));

const CHIP_COLORS_BY_TYPE = {
  monthly: {
    bgcolor: "shades.meadow.100",
    color: "shades.meadow.500",
  },
  annual: {
    bgcolor: "shades.secondary.100",
    color: "shades.secondary.500",
  },
  free_trial: {
    bgcolor: "shades.primary.100",
    color: "shades.primary.500",
  },
};

export const CadenceOrTrialChip = ({
  type,
}: {
  type?: BillingCadence | "free_trial";
}) => {
  if (!type) return null;

  const label = type === "free_trial" ? "Free trial" : capitalize(type);
  return <Chip {...CHIP_COLORS_BY_TYPE[type]}>{label}</Chip>;
};

type LicensesUsedProgressProps = LinearProgressProps & { error: boolean };

export const LicensesUsedProgress = styled<
  FunctionComponent<LicensesUsedProgressProps>
>(({ error: _, ...props }) => (
  <LinearProgress variant="determinate" {...props} />
))(({ error, theme, value }) => ({
  height: 8,
  borderRadius: 2,
  backgroundColor: theme.palette.shades.neutral[100],
  "& .MuiLinearProgress-bar": {
    borderRadius: 2,
    backgroundColor: error
      ? theme.palette.error.main
      : value === 100
        ? theme.palette.shades.primary[500]
        : theme.palette.shades.meadow[500],
  },
}));

type LicenseButtonProps = ButtonProps & { kind: "increment" | "decrement" };

export const LicenseCountButton = styled<FunctionComponent<LicenseButtonProps>>(
  ({ kind, ...props }) => (
    <MuiButton {...props}>{kind === "decrement" ? "–" : "+"}</MuiButton>
  )
)(({ kind, theme }) => ({
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  height: "48px",
  width: "48px",
  minWidth: "48px",
  fontSize: "1.25rem",
  color: theme.palette.shades.neutral[800],
  fontWeight: 400,
  ...(kind === "increment"
    ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }
    : { borderTopRightRadius: 0, borderBottomRightRadius: 0 }),
}));

const LicenseCountInputBase = styled(Input)({
  "& .MuiInputBase-root": {
    width: "fit-content",
    maxWidth: "100%",
  },
  "& .MuiInputBase-input": {
    textAlign: "center",

    /* Hide number input arrows in Chrome, Safari, Edge, Opera */
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    /* ...and in Firefox */
    MozAppearance: "textfield",
  },
});

interface LicenseCountInputProps {
  min: number;
  max?: number;
  value: InputProps["value"];
  onChange: InputProps["onChange"];
}

export const LicenseCountInput = styled<
  FunctionComponent<LicenseCountInputProps>
>(({ min, max, onChange, value, ...props }) => (
  <Row variant="center" {...props}>
    <LicenseCountInputBase
      disableUnderline
      type="number"
      inputProps={{ min, max }}
      onChange={onChange}
      value={value}
    />
  </Row>
))(({ theme }) => ({
  width: "60px",
  minWidth: "60px",
  height: "48px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  borderLeft: "none",
  borderRight: "none",
}));
